/**
 * LeasingCalendar — Übersicht der Leasing-Rückgabetermine.
 * Links: überfällige Termine (Datum < heute, noch nicht erledigt) als Extra-Spalte.
 * Rechts: Monats-/Wochen-/Tagesansicht mit eingetragenen Terminen + Center-Filter.
 * Klick auf einen Termin öffnet die Bearbeitung (umbuchen / Status) + „Vorgang öffnen".
 */
import { useEffect, useState, useCallback } from "react";
import { Loader2, ChevronLeft, ChevronRight, CalendarDays, AlertTriangle, X, MapPin, User, Bike, ExternalLink, Check, BellRing } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const WD = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
// „Heute" fest in Berlin-Zeit (Jul-2026, User-Wunsch) — unabhängig von der Browser-Zeitzone.
const todayStr = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin" }).format(new Date());
const vlabel = (v) => [v?.manufacturer, v?.model].filter(Boolean).join(" ") || "Fahrzeug";
const fmtDE = (iso) => { try { const [y, m, d] = iso.split("-"); return `${d}.${m}.${y}`; } catch { return iso; } };
const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => addDays(d, -((d.getDay() + 6) % 7)); // Montag

function monthMatrix(year, month) {
    const first = new Date(year, month, 1);
    const start = addDays(first, -((first.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

// Jul-2026 (User-Wunsch) — farbliche Markierung nach Leasinggesellschaft.
const companyChip = (name) => {
    const lc = (name || "").toLowerCase();
    if (lc.includes("santander")) return "bg-red-100 text-red-800 border-l-2 border-red-500";
    if (lc.includes("renault")) return "bg-yellow-100 text-yellow-900 border-l-2 border-yellow-500";
    if (lc.includes("auto bank") || lc.startsWith("ca")) return "bg-emerald-100 text-emerald-800 border-l-2 border-emerald-600";
    return "bg-indigo-100 text-indigo-700 border-l-2 border-indigo-400";
};
const companyDot = (name) => {
    const lc = (name || "").toLowerCase();
    if (lc.includes("santander")) return "bg-red-500";
    if (lc.includes("renault")) return "bg-yellow-400";
    if (lc.includes("auto bank") || lc.startsWith("ca")) return "bg-emerald-600";
    return "bg-indigo-400";
};

const chipClass = (a, today) => {
    if (a.status === "done") return "bg-emerald-50 text-emerald-600";
    const base = companyChip(a.leasing_company);
    return a.date < today ? `${base} ring-1 ring-red-500` : base;
};

export default function LeasingCalendar({ onOpenVorgang }) {
    const [appts, setAppts] = useState([]);
    const [busy, setBusy] = useState(true);
    const [view, setView] = useState("month"); // month | week | day
    const [anchor, setAnchor] = useState(new Date());
    const [centerFilter, setCenterFilter] = useState("");
    const [edit, setEdit] = useState(null);
    // Jul-2026 (User-Wunsch) — Schnellbuchung: Klick auf leeren Kalendertag.
    const [quickDate, setQuickDate] = useState(null);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/leasing-appointments");
            setAppts(data.appointments || []);
        } catch { /* noop */ } finally { setBusy(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const today = todayStr();
    const centers = [...new Set(appts.map((a) => a.center_name).filter(Boolean))].sort();
    const active = appts.filter((a) => a.status !== "cancelled" && (!centerFilter || a.center_name === centerFilter));
    const overdue = active.filter((a) => a.status !== "done" && a.date < today).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

    const byDate = {};
    for (const a of active) { (byDate[a.date] = byDate[a.date] || []).push(a); }
    for (const k of Object.keys(byDate)) byDate[k].sort((x, y) => (x.time || "").localeCompare(y.time || ""));

    const shift = (dir) => setAnchor((d) => view === "month" ? new Date(d.getFullYear(), d.getMonth() + dir, 1)
        : view === "week" ? addDays(d, dir * 7) : addDays(d, dir));
    const goToday = () => setAnchor(new Date(`${todayStr()}T00:00:00`));

    const headLabel = view === "month" ? `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`
        : view === "week" ? (() => { const s = startOfWeek(anchor); const e = addDays(s, 6); return `${fmtDE(isoOf(s))} – ${fmtDE(isoOf(e))}`; })()
            : fmtDE(isoOf(anchor));

    const openVorgang = async (recId) => {
        if (!recId) { toast.error("Kein verknüpfter Vorgang."); return; }
        try {
            const { data } = await api.get(`/leasing-returns/${recId}`);
            onOpenVorgang?.(data.return || data);
        } catch { toast.error("Vorgang konnte nicht geladen werden."); }
    };

    const Chip = ({ a }) => (
        <button onClick={() => setEdit(a)} title={`${a.time || ""} ${a.customer_name || ""}${a.leasing_company ? ` · ${a.leasing_company}` : ""}${a.record_type === "finance" ? " · Finanzierung" : ""}`} className={`w-full text-left text-[10px] leading-tight rounded-sm px-1 py-0.5 truncate font-semibold hover:brightness-95 ${chipClass(a, today)}`} data-testid={`leasing-cal-appt-${a.id}`}>
            {a.time ? `${a.time} ` : ""}{a.record_type === "finance" ? "FIN · " : ""}{a.customer_name || vlabel(a.vehicle)}
        </button>
    );

    // Legende: nur Gesellschaften mit vorhandenen Terminen anzeigen.
    const legendCompanies = [...new Set(active.map((a) => a.leasing_company || "Sonstige"))].sort();

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4" data-testid="leasing-calendar">
            {/* Überfällige Termine — linke Extra-Spalte */}
            <div className="border rounded-sm bg-white self-start" data-testid="leasing-cal-overdue">
                <div className="px-3 py-2 border-b bg-red-50 flex items-center gap-2 text-sm font-bold text-red-700">
                    <AlertTriangle className="w-4 h-4" /> Überfällige Termine
                    <span className="ml-auto text-xs bg-red-600 text-white rounded-full px-2 py-0.5" data-testid="leasing-cal-overdue-count">{overdue.length}</span>
                </div>
                <div className="max-h-[600px] overflow-y-auto divide-y">
                    {busy ? <div className="p-4 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                        : overdue.length === 0 ? <div className="p-4 text-sm text-gray-400" data-testid="leasing-cal-overdue-empty">Keine überfälligen Termine.</div>
                            : overdue.map((a) => (
                                <button key={a.id} onClick={() => setEdit(a)} className="w-full text-left p-3 hover:bg-red-50/60 transition-colors block" data-testid={`leasing-cal-overdue-${a.id}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-black text-red-700">{fmtDE(a.date)}{a.time ? ` · ${a.time}` : ""}</span>
                                        <span className="text-[10px] uppercase font-bold text-red-500">offen</span>
                                    </div>
                                    <div className="text-sm font-semibold text-gray-800 truncate">{a.customer_name || "—"}</div>
                                    <div className="text-xs text-gray-500 truncate">{vlabel(a.vehicle)} · {a.contract_number || "—"}</div>
                                    <div className="text-[11px] text-gray-400 truncate inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.center_name}</div>
                                </button>
                            ))}
                </div>
            </div>

            {/* Kalender */}
            <div className="border rounded-sm bg-white" data-testid="leasing-cal-grid">
                <div className="px-3 py-2 border-b flex items-center gap-2 flex-wrap">
                    <CalendarDays className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-black text-gray-800">{headLabel}</span>
                    {/* Ansicht-Umschalter */}
                    <div className="flex items-center gap-0.5 ml-2 border rounded-sm overflow-hidden">
                        {[["month", "Monat"], ["week", "Woche"], ["day", "Tag"]].map(([v, l]) => (
                            <button key={v} onClick={() => setView(v)} className={`text-xs font-bold px-2 py-1 ${view === v ? "bg-[#111827] text-white" : "text-gray-600 hover:bg-gray-50"}`} data-testid={`leasing-cal-view-${v}`}>{l}</button>
                        ))}
                    </div>
                    {/* Center-Filter */}
                    {centers.length > 0 && (
                        <select value={centerFilter} onChange={(e) => setCenterFilter(e.target.value)} className="text-xs border rounded-sm px-2 py-1 bg-white" data-testid="leasing-cal-center-filter">
                            <option value="">Alle Standorte</option>
                            {centers.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    )}
                    <div className="ml-auto flex items-center gap-1">
                        <button onClick={goToday} className="text-xs font-bold text-gray-600 border rounded-sm px-2 py-1 hover:bg-gray-50" data-testid="leasing-cal-today">Heute</button>
                        <button onClick={() => shift(-1)} className="border rounded-sm p-1 hover:bg-gray-50" data-testid="leasing-cal-prev"><ChevronLeft className="w-4 h-4" /></button>
                        <button onClick={() => shift(1)} className="border rounded-sm p-1 hover:bg-gray-50" data-testid="leasing-cal-next"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                </div>
                {/* Legende — vereinbarte Rückgabetermine, farblich nach Gesellschaft */}
                <div className="px-3 py-1.5 border-b bg-gray-50/60 flex items-center gap-3 flex-wrap text-[10px] font-bold text-gray-600" data-testid="leasing-cal-legend">
                    <span className="uppercase tracking-wider text-gray-400">Vereinbarte Rückgabetermine:</span>
                    {legendCompanies.map((c) => (
                        <span key={c} className="inline-flex items-center gap-1" data-testid={`leasing-cal-legend-${c}`}>
                            <span className={`w-2 h-2 rounded-full ${companyDot(c === "Sonstige" ? "" : c)}`} /> {c}
                        </span>
                    ))}
                    <span className="inline-flex items-center gap-1 text-gray-400">„FIN" = Finanzierungsvertrag · roter Rahmen = überfällig</span>
                </div>

                {/* MONATSANSICHT */}
                {view === "month" && (<>
                    <div className="grid grid-cols-7 text-center text-[11px] font-bold text-gray-400 border-b">
                        {WD.map((w) => <div key={w} className="py-1.5">{w}</div>)}
                    </div>
                    <div className="grid grid-cols-7">
                        {monthMatrix(anchor.getFullYear(), anchor.getMonth()).map((d, i) => {
                            const iso = isoOf(d);
                            const inMonth = d.getMonth() === anchor.getMonth();
                            const isToday = iso === today;
                            const list = byDate[iso] || [];
                            return (
                                <div key={i} onClick={() => setQuickDate(iso)} title="Klick: Rückgabetermin buchen (nur Leasing)" className={`min-h-[92px] border-b border-r p-1 cursor-pointer hover:bg-indigo-50/50 group ${inMonth ? "bg-white" : "bg-gray-50/60"} ${isToday ? "ring-2 ring-inset ring-indigo-400" : ""}`} data-testid={`leasing-cal-day-${iso}`}>
                                    <div className="flex items-center justify-between">
                                        <span className={`text-[11px] font-bold ${inMonth ? "text-gray-600" : "text-gray-300"} ${isToday ? "text-indigo-600" : ""}`}>{d.getDate()}</span>
                                        <span className="text-[10px] font-black text-indigo-400 opacity-0 group-hover:opacity-100">+ Termin</span>
                                    </div>
                                    <div className="space-y-1 mt-0.5" onClick={(e) => e.stopPropagation()}>
                                        {list.slice(0, 3).map((a) => <Chip key={a.id} a={a} />)}
                                        {list.length > 3 && <div className="text-[10px] text-gray-400 pl-1">+{list.length - 3} weitere</div>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>)}

                {/* WOCHENANSICHT */}
                {view === "week" && (
                    <div className="grid grid-cols-7" data-testid="leasing-cal-week">
                        {Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i)).map((d) => {
                            const iso = isoOf(d);
                            const isToday = iso === today;
                            const list = byDate[iso] || [];
                            return (
                                <div key={iso} className={`min-h-[260px] border-r last:border-r-0 ${isToday ? "bg-indigo-50/40" : ""}`} data-testid={`leasing-cal-week-day-${iso}`}>
                                    <div className={`text-center py-1.5 border-b text-[11px] font-bold ${isToday ? "text-indigo-600" : "text-gray-500"}`}>{WD[(d.getDay() + 6) % 7]} {d.getDate()}.</div>
                                    <div className="p-1 space-y-1">
                                        {list.length === 0 ? <div className="text-[10px] text-gray-300 text-center pt-2">—</div> : list.map((a) => <Chip key={a.id} a={a} />)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* TAGESANSICHT */}
                {view === "day" && (
                    <div className="p-3" data-testid="leasing-cal-day-view">
                        {(byDate[isoOf(anchor)] || []).length === 0 ? (
                            <div className="text-sm text-gray-400 py-8 text-center">Keine Termine an diesem Tag.</div>
                        ) : (
                            <ul className="divide-y">
                                {(byDate[isoOf(anchor)] || []).map((a) => (
                                    <li key={a.id}>
                                        <button onClick={() => setEdit(a)} className="w-full text-left py-2.5 px-2 hover:bg-gray-50 flex items-center gap-3" data-testid={`leasing-cal-day-appt-${a.id}`}>
                                            <span className={`text-xs font-black rounded-sm px-2 py-1 ${chipClass(a, today)}`}>{a.time || "—"}</span>
                                            <span className="flex-1 min-w-0">
                                                <span className="block text-sm font-semibold text-gray-800 truncate">{a.customer_name || "—"}</span>
                                                <span className="block text-xs text-gray-500 truncate">{vlabel(a.vehicle)} · {a.center_name}</span>
                                            </span>
                                            {a.status === "done" && <Check className="w-4 h-4 text-emerald-600" />}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {edit && <EditModal appt={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} onOpenVorgang={openVorgang} />}
            {quickDate && <QuickBookModal date={quickDate} onClose={() => setQuickDate(null)} onSaved={() => { setQuickDate(null); load(); }} />}
        </div>
    );
}

// Jul-2026 (User-Wunsch) — Schnellbuchung: Vorgang suchen (NUR Leasing) + Termin setzen.
function QuickBookModal({ date, onClose, onSaved }) {
    const [d, setD] = useState(date);
    const [time, setTime] = useState("09:00");
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [sel, setSel] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const t = setTimeout(async () => {
            if (q.trim().length < 2) { setResults([]); return; }
            setSearching(true);
            try {
                const { data } = await api.get("/leasing-returns", { params: { q: q.trim(), page_size: 8, record_type: "leasing" } });
                setResults((data.items || []).filter((r) => r.status !== "decided"));
            } catch { /* noop */ } finally { setSearching(false); }
        }, 300);
        return () => clearTimeout(t);
    }, [q]);

    const save = async () => {
        if (!sel) { toast.error("Bitte zuerst einen Leasing-Vorgang auswählen."); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/leasing-returns/${sel.id}/appointment`, { date: d, time });
            toast.success(`Rückgabetermin am ${fmtDE(d)}${time ? ` · ${time}` : ""} gebucht.${data?.mail_sent ? ` Terminbestätigung mit Checkliste an ${data.sent_to} gesendet.` : ""}`);
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "Buchung fehlgeschlagen."); }
        finally { setBusy(false); }
    };

    const label = (r) => {
        const c = r.customer || {};
        const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
        return { name, veh: vlabel(r.vehicle), extra: [r.contract_number, r.leasing_company].filter(Boolean).join(" · ") };
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4" data-testid="leasing-cal-quickbook">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <div className="font-black text-gray-900">Rückgabetermin buchen</div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="leasing-cal-quickbook-close"><X className="w-5 h-5" /></button>
                </div>
                <p className="text-[11px] text-gray-500 -mt-2">Nur für <strong>Leasing-Rückgaben</strong> — Finanzierungsverträge haben keine Rückgabe.</p>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-[11px] uppercase font-bold text-gray-400">Datum</label>
                        <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="rounded-sm text-sm" data-testid="leasing-cal-quickbook-date" />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase font-bold text-gray-400">Uhrzeit</label>
                        <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="rounded-sm text-sm" data-testid="leasing-cal-quickbook-time" />
                    </div>
                </div>
                <div>
                    <label className="text-[11px] uppercase font-bold text-gray-400">Leasing-Vorgang suchen</label>
                    <div className="relative">
                        <Input value={q} onChange={(e) => { setQ(e.target.value); setSel(null); }} placeholder="Kunde, Vertragsnr., FIN, Modell …" className="rounded-sm text-sm" data-testid="leasing-cal-quickbook-search" />
                        {searching && <Loader2 className="w-4 h-4 animate-spin absolute right-2 top-2.5 text-gray-400" />}
                    </div>
                    {sel ? (
                        <div className="mt-2 border border-emerald-300 bg-emerald-50 rounded-sm p-2 text-sm" data-testid="leasing-cal-quickbook-selected">
                            <div className="font-bold">{label(sel).name}</div>
                            <div className="text-xs text-gray-600">{label(sel).veh} · {label(sel).extra}</div>
                            {sel.return_appointment_date && <div className="text-[11px] text-amber-700 font-bold mt-1">Achtung: Termin am {fmtDE(sel.return_appointment_date)} wird überschrieben.</div>}
                        </div>
                    ) : results.length > 0 && (
                        <div className="mt-1 border rounded-sm divide-y max-h-52 overflow-auto" data-testid="leasing-cal-quickbook-results">
                            {results.map((r) => (
                                <button key={r.id} onClick={() => setSel(r)} className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm" data-testid={`leasing-cal-quickbook-result-${r.id}`}>
                                    <span className="font-bold">{label(r).name}</span>
                                    <span className="block text-xs text-gray-500">{label(r).veh} · {label(r).extra}{r.has_return_appointment ? " · hat bereits Termin" : ""}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="leasing-cal-quickbook-abort">Abbrechen</Button>
                    <Button onClick={save} disabled={busy || !sel} className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="leasing-cal-quickbook-save">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CalendarDays className="w-4 h-4 mr-1" />} Termin buchen
                    </Button>
                </div>
            </div>
        </div>
    );
}

function EditModal({ appt, onClose, onSaved, onOpenVorgang }) {
    const [date, setDate] = useState(appt.date);
    const [time, setTime] = useState(appt.time || "");
    const [busy, setBusy] = useState(false);
    const [lead, setLead] = useState(14);
    const [reminding, setReminding] = useState(false);

    const patch = async (payload, msg) => {
        setBusy(true);
        try { await api.patch(`/leasing-appointments/${appt.id}`, payload); toast.success(msg); onSaved(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen."); }
        finally { setBusy(false); }
    };

    const remind = async () => {
        setReminding(true);
        try {
            const { data } = await api.post(`/leasing-appointments/${appt.id}/remind`, { lead_days: Number(lead) || 0 });
            toast.success(data.scheduled
                ? `Erinnerung geplant — ${data.lead_days} Tage vor dem Termin (Aufgabe + Kunden-Mail-Entwurf).`
                : "Erinnerung angelegt — Mitarbeiter-Aufgabe + Kunden-Mail-Entwurf erstellt.");
        } catch (e) { toast.error(e?.response?.data?.detail || "Erinnerung fehlgeschlagen."); }
        finally { setReminding(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4" data-testid="leasing-cal-edit-modal">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <div className="font-black text-gray-900">Rückgabetermin bearbeiten</div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="leasing-cal-edit-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="text-sm space-y-1 bg-gray-50 rounded-sm p-3">
                    <div className="flex items-center gap-2 font-semibold text-gray-800"><User className="w-4 h-4 text-gray-400" /> {appt.customer_name || "—"}</div>
                    <div className="flex items-center gap-2 text-gray-600"><Bike className="w-4 h-4 text-gray-400" /> {[appt.vehicle?.manufacturer, appt.vehicle?.model].filter(Boolean).join(" ") || "Fahrzeug"} · {appt.contract_number || "—"}</div>
                    <div className="flex items-center gap-2 text-gray-600"><MapPin className="w-4 h-4 text-gray-400" /> {appt.center_name}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-[11px] uppercase font-bold text-gray-400">Datum</label>
                        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm text-sm" data-testid="leasing-cal-edit-date" />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase font-bold text-gray-400">Uhrzeit</label>
                        <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="rounded-sm text-sm" data-testid="leasing-cal-edit-time" />
                    </div>
                </div>

                {/* Erinnerung planen / auslösen */}
                <div className="border rounded-sm bg-indigo-50/40 p-3 space-y-2" data-testid="leasing-cal-remind-box">
                    <div className="text-[11px] uppercase font-bold text-gray-500 inline-flex items-center gap-1.5"><BellRing className="w-3.5 h-3.5" /> Erinnerung</div>
                    <div className="flex items-center gap-2">
                        <select value={lead} onChange={(e) => setLead(e.target.value)} className="text-sm border rounded-sm px-2 py-1.5 bg-white flex-1" data-testid="leasing-cal-remind-lead">
                            <option value={0}>Sofort erinnern</option>
                            <option value={1}>1 Tag vorher</option>
                            <option value={3}>3 Tage vorher</option>
                            <option value={7}>1 Woche vorher</option>
                            <option value={14}>2 Wochen vorher</option>
                        </select>
                        <Button onClick={remind} disabled={reminding} variant="outline" className="rounded-sm border-indigo-300 text-indigo-700 hover:bg-indigo-100" data-testid="leasing-cal-remind-btn">
                            {reminding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <BellRing className="w-4 h-4 mr-1" />} Erinnern
                        </Button>
                    </div>
                    <p className="text-[10px] text-gray-400">Legt eine Mitarbeiter-Aufgabe an und – falls E-Mail vorhanden – einen Kunden-Erinnerungs-Entwurf (Versand nach Freigabe im E-Mails-Tab).</p>
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                    <Button onClick={() => patch({ date, time }, "Termin umgebucht.")} disabled={busy} className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="leasing-cal-edit-save">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Umbuchen
                    </Button>
                    {appt.status !== "done" && (
                        <Button onClick={() => patch({ status: "done" }, "Als erledigt markiert.")} disabled={busy} variant="outline" className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="leasing-cal-edit-done">
                            <Check className="w-4 h-4 mr-1" /> Erledigt
                        </Button>
                    )}
                    <Button onClick={() => patch({ status: "cancelled" }, "Termin storniert.")} disabled={busy} variant="ghost" className="rounded-sm text-red-600 hover:bg-red-50" data-testid="leasing-cal-edit-cancel">
                        Stornieren
                    </Button>
                    <Button onClick={() => onOpenVorgang(appt.leasing_return_id)} variant="ghost" className="rounded-sm text-gray-700 ml-auto" data-testid="leasing-cal-edit-open-vorgang">
                        <ExternalLink className="w-4 h-4 mr-1" /> Vorgang öffnen
                    </Button>
                </div>
            </div>
        </div>
    );
}
